import React, { useState } from 'react';
import {
    ArrowLeft, Plus, Trash2, Save, Download,
    AlertTriangle, CheckCircle
} from 'lucide-react';
import { API_BASE_URL } from '../../api/client';

export default function ManualInvoiceDetails({ invoice, onBack }) {
    const [items, setItems] = useState(invoice.items || []);
    const [loading, setLoading] = useState(false);
    const [showAddItem, setShowAddItem] = useState(false);

    // New Item Form State
    const [newItem, setNewItem] = useState({
        nature_poste: '',
        montant_ht: '',
        taux_tva: invoice.taux_tva_default || '19',
        description: '',
        montant_remboursable: true,
        motif_non_remboursable: ''
    });

    const NATURE_POSTE_OPTIONS = [
        "Pièce", "Main d'œuvre", "Peinture", "Diagnostic",
        "Consommable", "Remorquage", "Autre"
    ];

    const MOTIF_OPTIONS = [
        "Franchise", "Déduction vétusté", "Pièce non admise",
        "Main d'œuvre excessive", "Réduction commerciale", "Autre"
    ];

    const handleAddItem = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const payload = {
                ...newItem,
                montant_ht: parseFloat(newItem.montant_ht),
                taux_tva: parseFloat(newItem.taux_tva),
                invoice_id: invoice.id
            };

            const response = await fetch(`${API_BASE_URL}/manual-invoices/${invoice.id}/items`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const addedItem = await response.json();
                setItems([...items, addedItem]);
                setShowAddItem(false);
                setNewItem({
                    nature_poste: '',
                    montant_ht: '',
                    taux_tva: invoice.taux_tva_default || '19',
                    description: '',
                    montant_remboursable: true,
                    motif_non_remboursable: ''
                });
            }
        } catch (error) {
            console.error("Error adding item:", error);
            alert("Erreur lors de l'ajout de la ligne");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteItem = async (itemId) => {
        if (!window.confirm("Supprimer cette ligne ?")) return;

        try {
            const response = await fetch(`${API_BASE_URL}/manual-invoices/items/${itemId}`, {
                method: 'DELETE'
            });

            if (response.ok) {
                setItems(items.filter(item => item.id !== itemId));
            }
        } catch (error) {
            console.error("Error deleting item:", error);
        }
    };

    const calculateTotals = () => {
        const totalHT = items.reduce((sum, item) => sum + (item.montant_ht || 0), 0);
        const totalTVA = items.reduce((sum, item) => sum + ((item.montant_ht || 0) * (item.taux_tva || 0) / 100), 0);
        const totalTTC = totalHT + totalTVA;
        return { totalHT, totalTVA, totalTTC };
    };

    const { totalHT, totalTVA, totalTTC } = calculateTotals();

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 min-h-[500px] flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gray-50 rounded-t-lg">
                <div className="flex items-center gap-3">
                    <button
                        onClick={onBack}
                        className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors text-gray-600"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <h3 className="font-bold text-gray-800 text-lg">
                            {invoice.numero_facture}
                        </h3>
                        <p className="text-xs text-gray-500 flex gap-2">
                            <span>{invoice.reparateur_nom}</span>
                            <span>•</span>
                            <span>{invoice.date_facture}</span>
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-4">
                    <div className="text-right">
                        <p className="text-xs text-gray-500 uppercase font-bold">Total TTC</p>
                        <p className="text-lg font-bold text-blue-600">
                            {totalTTC.toLocaleString('fr-FR', { style: 'currency', currency: 'TND' })}
                        </p>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="p-6 flex-1">
                {/* Invoice Metadata Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 bg-blue-50/50 p-4 rounded-lg border border-blue-100">
                    <div>
                        <span className="text-xs text-gray-400 uppercase font-bold block mb-1">Matricule Fiscale</span>
                        <span className="text-sm font-medium text-gray-700">{invoice.matricule_fiscale || "-"}</span>
                    </div>
                    <div>
                        <span className="text-xs text-gray-400 uppercase font-bold block mb-1">N° Devis</span>
                        <span className="text-sm font-medium text-gray-700">{invoice.numero_devis || "-"}</span>
                    </div>
                    <div>
                        <span className="text-xs text-gray-400 uppercase font-bold block mb-1">TVA Défaut</span>
                        <span className="text-sm font-medium text-gray-700">{invoice.taux_tva_default}%</span>
                    </div>
                    <div>
                        <span className="text-xs text-gray-400 uppercase font-bold block mb-1">Adresse</span>
                        <span className="text-sm font-medium text-gray-700 truncate" title={invoice.adresse}>{invoice.adresse || "-"}</span>
                    </div>
                    {invoice.commentaire && (
                        <div className="col-span-2 md:col-span-4 mt-2 pt-2 border-t border-blue-200/50">
                            <span className="text-xs text-gray-400 uppercase font-bold block mb-1">Commentaire</span>
                            <span className="text-sm italic text-gray-600">{invoice.commentaire}</span>
                        </div>
                    )}
                </div>

                {/* Items Table */}
                <div className="overflow-x-auto border border-gray-200 rounded-lg mb-6">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nature</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Montant HT</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">TVA (%)</th>
                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Remboursable</th>
                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-10"></th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {items.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="px-4 py-8 text-center text-gray-400 text-sm">
                                        Aucune ligne pour le moment.
                                    </td>
                                </tr>
                            ) : (
                                items.map((item) => (
                                    <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {item.nature_poste}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-500">
                                            {item.description || "-"}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">
                                            {item.montant_ht?.toLocaleString('fr-FR', { minimumFractionDigits: 3 })}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-500">
                                            {item.taux_tva}%
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-center">
                                            {item.montant_remboursable ? (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                                    Oui
                                                </span>
                                            ) : (
                                                <div className="flex flex-col items-center">
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 mb-1">
                                                        Non
                                                    </span>
                                                    <span className="text-[10px] text-gray-500 max-w-[100px] truncate" title={item.motif_non_remboursable}>
                                                        {item.motif_non_remboursable}
                                                    </span>
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-center">
                                            <button
                                                onClick={() => handleDeleteItem(item.id)}
                                                className="text-gray-400 hover:text-red-600 transition-colors"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                        <tfoot className="bg-gray-50 border-t border-gray-200">
                            <tr>
                                <td colSpan="2" className="px-4 py-3 text-right text-sm font-bold text-gray-700">TOTAL</td>
                                <td className="px-4 py-3 text-right text-sm font-bold text-gray-900">
                                    {totalHT.toLocaleString('fr-FR', { minimumFractionDigits: 3 })}
                                </td>
                                <td className="px-4 py-3 text-right text-sm font-bold text-gray-500">
                                    {totalTVA.toLocaleString('fr-FR', { minimumFractionDigits: 3 })} (TVA)
                                </td>
                                <td colSpan="2" className="px-4 py-3 text-right text-sm font-bold text-blue-600">
                                    {totalTTC.toLocaleString('fr-FR', { style: 'currency', currency: 'TND' })} TTC
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                {/* Add Item Button / Form */}
                {!showAddItem ? (
                    <button
                        onClick={() => setShowAddItem(true)}
                        className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 font-medium hover:border-blue-500 hover:text-blue-500 transition-colors flex items-center justify-center gap-2"
                    >
                        <Plus className="w-5 h-5" />
                        Ajouter une ligne
                    </button>
                ) : (
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 animate-in fade-in slide-in-from-top-2 duration-200">
                        <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                            <Plus className="w-4 h-4" /> Nouvelle Ligne
                        </h4>
                        <form onSubmit={handleAddItem} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                {/* Nature du poste */}
                                <div className="md:col-span-1">
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Nature du poste *
                                    </label>
                                    <select
                                        required
                                        value={newItem.nature_poste}
                                        onChange={(e) => setNewItem({ ...newItem, nature_poste: e.target.value })}
                                        className="w-full text-sm border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    >
                                        <option value="">Sélectionner...</option>
                                        {NATURE_POSTE_OPTIONS.map(opt => (
                                            <option key={opt} value={opt}>{opt}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Montant HT */}
                                <div className="md:col-span-1">
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Montant HT *
                                    </label>
                                    <input
                                        type="number"
                                        step="0.001"
                                        required
                                        value={newItem.montant_ht}
                                        onChange={(e) => setNewItem({ ...newItem, montant_ht: e.target.value })}
                                        className="w-full text-sm border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    />
                                </div>

                                {/* Taux TVA */}
                                <div className="md:col-span-1">
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Taux TVA (%) *
                                    </label>
                                    <input
                                        type="number"
                                        step="0.1"
                                        required
                                        value={newItem.taux_tva}
                                        onChange={(e) => setNewItem({ ...newItem, taux_tva: e.target.value })}
                                        className="w-full text-sm border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    />
                                </div>

                                {/* Remboursable Toggle */}
                                <div className="md:col-span-1 flex items-center h-full pt-6">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={newItem.montant_remboursable}
                                            onChange={(e) => setNewItem({
                                                ...newItem,
                                                montant_remboursable: e.target.checked,
                                                motif_non_remboursable: e.target.checked ? '' : newItem.motif_non_remboursable
                                            })}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-gray-700 font-medium">Montant remboursable</span>
                                    </label>
                                </div>

                                {/* Motif Non Remboursable (Conditionnel) */}
                                {!newItem.montant_remboursable && (
                                    <div className="md:col-span-2 md:col-start-3 animate-in fade-in">
                                        <label className="block text-xs font-medium text-red-600 mb-1 flex items-center gap-1">
                                            <AlertTriangle className="w-3 h-3" /> Motif de non-remboursement *
                                        </label>
                                        <select
                                            required={!newItem.montant_remboursable}
                                            value={newItem.motif_non_remboursable}
                                            onChange={(e) => setNewItem({ ...newItem, motif_non_remboursable: e.target.value })}
                                            className="w-full text-sm border-red-300 bg-red-50 rounded-lg shadow-sm focus:border-red-500 focus:ring-red-500 text-red-900"
                                        >
                                            <option value="">Sélectionner...</option>
                                            {MOTIF_OPTIONS.map(opt => (
                                                <option key={opt} value={opt}>{opt}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                {/* Description */}
                                <div className="md:col-span-4">
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Description
                                    </label>
                                    <input
                                        type="text"
                                        value={newItem.description}
                                        onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                                        className="w-full text-sm border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    />
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 mt-4">
                                <button
                                    type="button"
                                    onClick={() => setShowAddItem(false)}
                                    className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                    disabled={loading}
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                                >
                                    {loading ? "Ajout..." : "Ajouter la ligne"}
                                </button>
                            </div>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
}
